import type { FetchQuoteParams } from 'actions/InsuranceActions'
import { fetchInsuranceQuote } from 'actions/InsuranceActions'
import { INSURANCE_PRODUCT_ID } from 'constants/insurance'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { getInsuranceQuoteKey } from 'lib/insurance/insuranceUtils'
import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import useInsuranceProducts from './useInsuranceProducts'
import CurrencyContext from 'contexts/currencyContext'
import { isCheckout, getCartId } from 'selectors/checkoutSelectors'

interface BedbankQuoteParams {
  country: string
  coverAmount: number
  checkIn?: string
  checkout?: string
  travellers: App.Occupants | Array<App.Occupants>
  useCache?: boolean
  disabled?: boolean
}

function useRoomOptionRateBedbankQuote(params: BedbankQuoteParams) {
  const { country, coverAmount, checkIn, checkout, travellers, useCache, disabled } = params

  const dispatch = useAppDispatch()

  const [isInsuranceSelected, setIsInsuranceSelected] = useState(false)

  useInsuranceProducts('booking-protection', { disabled })

  // Build the quote parameters based on the hook input parameters
  const currency = useContext(CurrencyContext)
  const insideCheckout = useAppSelector(isCheckout)
  const checkoutCartId = useAppSelector(getCartId)
  const quoteParams: FetchQuoteParams | undefined = useMemo(() => {
    const areDatesAdded = !!checkIn && !!checkout
    if (!areDatesAdded || disabled) return

    return {
      startDate: checkIn,
      endDate: checkout,
      travellers,
      currency,
      destinationCountries: [country],
      coverAmount,
      type: 'booking-protection',
      cartId: insideCheckout ? checkoutCartId : undefined,
      useCache,
    }
  }, [checkIn, checkout, disabled, travellers, currency, country, coverAmount, insideCheckout, checkoutCartId, useCache])

  // Fetch the CFMR insurance quote when the component mounts or parameters change
  useEffect(() => {
    if (!quoteParams || disabled) return

    dispatch(fetchInsuranceQuote(INSURANCE_PRODUCT_ID.CFMR, quoteParams))
  }, [dispatch, quoteParams, disabled])

  // Get the same key that was used in actions to store data, so we can retrieve the correct quote, loading state, and errors from Redux state
  const key = useMemo(() => {
    if (!quoteParams) return

    return getInsuranceQuoteKey(INSURANCE_PRODUCT_ID.CFMR, quoteParams)
  }, [quoteParams])

  // Get the quote data for this specific key
  const quotes = useAppSelector(state => state.insurance.quotes)
  const quote = useMemo(() => {
    if (!key) return

    return quotes[key]
  }, [key, quotes])

  // Get the loading state for this specific quote
  const fetchingQuotes = useAppSelector(state => state.insurance.fetchingQuotes)
  const isFetching = useMemo(() => {
    if (!key) return

    return !!fetchingQuotes[key]
  }, [fetchingQuotes, key])

  // Get any errors that occurred while fetching this quote
  const fetchQuoteErrors = useAppSelector(state => state.insurance.fetchQuotesErrors)
  const error = useMemo(() => {
    if (!key) return

    return fetchQuoteErrors[key]
  }, [fetchQuoteErrors, key])

  const onSelectInsurance = useCallback((selectedValue: 'non-refundable' | 'full-refund') => {
    if (selectedValue === 'full-refund') setIsInsuranceSelected(true)
    if (selectedValue === 'non-refundable') setIsInsuranceSelected(false)
  }, [])

  return {
    quote,
    // Handle the initial loading state: when the component first loads, we're in an idle state
    // where the fetch hasn't been dispatched yet and we don't have quotes or errors in Redux state.
    // In this case, we consider it as "fetching" because we're in the gap between component mount and the initial fetch.
    isFetching: (!quote && !error) || !!isFetching,
    error,
    isInsuranceSelected,
    onSelectInsurance,
  }
}

export default useRoomOptionRateBedbankQuote
